import React, { useEffect, useRef } from "react";
import _, { set } from "lodash";
import { useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";
import { connect } from "react-redux";

import { Track } from "@music/models";
import { messages as mMessages } from "@music/messages";
import { Table } from "@ui/Table";
import { CellBasicNumber, CellViewLink } from "@ui/Table/Cells";
import { compose } from "@util/compose";
import { SalesState } from "../../models";
import { messages } from "../../messages";
import { SaleRow, getAggregateSalesForTable, getSalesTypes } from "../../selectors";

interface TrackSalesTableProps {
    albumId: number;
    tracks: Track[];
}

interface TrackSalesTableStateProps {
    data: SaleRow[];
    types: string[];
}

// Type the table component
type TypedTable = new () => Table<SaleRow>;
const TypedTable = Table as TypedTable;

type Props = TrackSalesTableProps & TrackSalesTableStateProps;

const TrackSalesTable = ({ data, types }: Props) => {
    const intl = useIntl();
    const isMounted = useRef(false);
    const key = useRef(1);
    useEffect(() => {
        if (isMounted.current) {
            key.current += 1;
        }
        else {
            isMounted.current = true;
        }
    }, [types]);
    let cols = [
        <ColumnDefinition
            key="disc"
            id="entity.pivot.disc"
            title={intl.formatMessage(mMessages.albumTrackColumnDisc)}
            customComponent={CellBasicNumber} />,
        <ColumnDefinition
            key="track"
            id="entity.pivot.track"
            title={intl.formatMessage(mMessages.albumTrackColumnTrack)}
            customComponent={CellBasicNumber} />,
        <ColumnDefinition
            key="title"
            id="entity.title"
            title={intl.formatMessage(mMessages.trackColumnTitle)}
            customComponent={CellViewLink} extraData={{entity: "track"}} />,
        <ColumnDefinition
            key="isrc"
            id="entity.isrc"
            title={intl.formatMessage(mMessages.trackColumnIsrc)} />,
    ];
    cols = cols.concat(
        _.map(types, (type) => (
            <ColumnDefinition key={type} id={"sales.types." + type}
                title={intl.formatMessage({ id: "sales.graph." + type })}
                customComponent={CellBasicNumber} />
        )),
    );
    cols.push(
        <ColumnDefinition
            key="total"
            id="sales.total"
            title={intl.formatMessage(messages.salesTotal)}
            customComponent={CellBasicNumber} />,
    );

    const defaultSorting = [
        { id: "entity.pivot.disc", sortAscending: true },
        { id: "entity.pivot.track", sortAscending: true },
    ];

    return (
        <div className="track-sales-table">
            <TypedTable key={key.current} data={data} layout="basic"
                defaultSorting={defaultSorting}
                pageSize={64}
            >
                <RowDefinition>
                    {cols}
                </RowDefinition>
            </TypedTable>
        </div>
    );
};

const mapStateToProps = (state: SalesState, props: TrackSalesTableProps) => {
    const newProps = {
        albumId: props.albumId,
        tracks: props.tracks,
    };
    const types = getSalesTypes(state, newProps);
    return {
        data: getAggregateSalesForTable(state, newProps),
        types: types == null ? [] : types,
    } as TrackSalesTableStateProps;
};

const ConnectedTrackSalesTable = compose<Props, TrackSalesTableProps>(
    connect(mapStateToProps, null),
)(TrackSalesTable);

export {
    TrackSalesTable as TrackSalesTablePresenter,
    ConnectedTrackSalesTable as TrackSalesTable,
    Props as TrackSalesTableProps,
};
